import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLatest } from '../useLatest'
import { useBruktbilFilters } from '../useBruktbilFilters'
import {
  fetchStaatidMerker,
  fetchStaatidForhandlere,
  fetchStaatidForhandlereTrend,
  fetchStaatidOversikt,
  fetchStaatidRoundtrips,
  loadScatterSample,
  monthRange,
  formatDager,
  formatDekningsgrad,
  DRIVLINJE_OPTIONS,
  UNKNOWN_ERROR,
} from './api'
import type {
  StaatidParams,
  StaatidMerkerResponse,
  StaatidOversiktResponse,
  StaatidRoundtripsResponse,
  StaatidForhandlerRow,
} from './api'
import MerkeScatter from './MerkeScatter'
import TrailPanel from './TrailPanel'
import MultiSelectTypeahead from './MultiSelectTypeahead'
import { ChipGroup } from './FilterChip'

const DEBOUNCE_MS = 300
const ANTALL_OPTIONS = [
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5+' },
] as const

interface Dealer {
  orgNr: string
  navn: string | null
}

interface Query {
  merke: string[]
  modell: string[]
  // Multi-select (#274 dealer-comparison follow-up): each selected dealer
  // becomes its own trend line on the scatter, same pattern as merke/modell.
  dealers: Dealer[]
  aarsmodellMin?: number
  aarsmodellMax?: number
  antallTransfers: number[]
  drivstoffGruppe: string[] // #800
}

const EMPTY_QUERY: Query = { merke: [], modell: [], dealers: [], antallTransfers: [], drivstoffGruppe: [] }

function toParams(base: StaatidParams, q: Query): StaatidParams {
  return {
    ...base,
    merke: q.merke.length ? q.merke : undefined,
    modell: q.modell.length ? q.modell : undefined,
    dealerOrgNr: q.dealers.length ? q.dealers.map(d => d.orgNr) : undefined,
    aarsmodellMin: q.aarsmodellMin,
    aarsmodellMax: q.aarsmodellMax,
    antallTransfers: q.antallTransfers.length ? q.antallTransfers : undefined,
    drivstoffGruppe: q.drivstoffGruppe.length ? q.drivstoffGruppe : undefined,
  }
}

/**
 * Free query over the ståtid observations: merke/modell (multi), forhandler
 * (typeahead on name/org.nr), årsmodell range and antall ledd — the result is
 * a scatter with per-merke colors and monthly-median trend lines.
 */
export default function SporringTab() {
  const { t } = useTranslation('staatid')
  const { from, to, selgerPostnummer } = useBruktbilFilters()
  const base: StaatidParams = { from, to, selgerPostnummer }
  const baseKey = `${from}|${to}|${selgerPostnummer ?? ''}`

  const [draft, setDraft] = useState<Query>(EMPTY_QUERY)
  const [applied, setApplied] = useState<Query | null>(null)

  // Option sources
  const [merkeOptions, setMerkeOptions] = useState<string[]>([])
  const [modellOptions, setModellOptions] = useState<string[]>([])
  const [dealerQuery, setDealerQuery] = useState('')
  const [dealerOptions, setDealerOptions] = useState<StaatidForhandlerRow[]>([])
  const [dealerOpen, setDealerOpen] = useState(false)

  // Results
  const [merker, setMerker] = useState<StaatidMerkerResponse | null>(null)
  const [oversikt, setOversikt] = useState<StaatidOversiktResponse | null>(null)
  const [scatter, setScatter] = useState<StaatidRoundtripsResponse | null>(null)
  const [zoomMonth, setZoomMonth] = useState<string | null>(null)
  const [trailSluttSvvId, setTrailSluttSvvId] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Merke options: all merker in the period, by volume.
  useLatest(alive => {
    fetchStaatidMerker(base, [], 500, 0)
      .then(m => {
        if (alive()) setMerkeOptions(m.data.rows.map(r => r.merke).filter((x): x is string => !!x))
      })
      .catch(() => { if (alive()) setMerkeOptions([]) })
  }, [baseKey])

  // Modell options follow the drafted merke selection (drilldown response).
  // Selections aren't cleared when a merke is ADDED — only pruned here if a
  // modell no longer belongs to any selected merke (e.g. its merke was
  // removed) — so picking VOLVO EX90, then adding VOLKSWAGEN for GOLD,
  // keeps EX90 instead of wiping it (found via user testing on #457).
  useLatest(alive => {
    if (!draft.merke.length) {
      setModellOptions([])
      setDraft(d => (d.modell.length ? { ...d, modell: [] } : d))
      return
    }
    fetchStaatidMerker(base, draft.merke, 500, 0)
      .then(m => {
        if (!alive()) return
        const options = m.data.rows.map(r => r.modell).filter((x): x is string => !!x)
        setModellOptions(options)
        setDraft(d => {
          const kept = d.modell.filter(x => options.includes(x))
          return kept.length === d.modell.length ? d : { ...d, modell: kept }
        })
      })
      .catch(() => { if (alive()) setModellOptions([]) })
  }, [baseKey, JSON.stringify(draft.merke)])

  // Dealer typeahead — name contains / org_nr prefix (backend q).
  useLatest(alive => {
    if (!dealerQuery) {
      setDealerOptions([])
      return
    }
    const timer = setTimeout(() => {
      fetchStaatidForhandlere(base, 'antall_roundtrips', 'desc', 20, 0, dealerQuery)
        .then(f => { if (alive()) setDealerOptions(f.data.rows) })
        .catch(() => { if (alive()) setDealerOptions([]) })
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [baseKey, dealerQuery])

  // Series dimension: dealer x merke pairs (#471) when 2+ of each are
  // picked together, plain dealer comparison (#460/#470) when only 1 merke
  // is active, else modell-level (drilldown), else merke.
  const groupBy: 'merke' | 'modell' | 'dealer' | 'dealer-merke' = applied?.dealers.length
    ? (applied.merke.length >= 2 ? 'dealer-merke' : 'dealer')
    : applied?.modell.length ? 'modell' : 'merke'

  // runQuery is imperative — it is called from an effect, from "Vis resultater"
  // and from the scatter's month zoom — so useLatest cannot guard it. A request
  // token does the same job: only the most recently started run is allowed to
  // write. Without it, a slow first query can land after a newer one and
  // repaint the chart with the wrong period's data, and every superseded call
  // still holds a DB connection until it finishes.
  const runId = useRef(0)

  function runQuery(q: Query, month: string | null) {
    const thisRun = ++runId.current
    const alive = () => runId.current === thisRun
    const params = month
      ? { ...toParams(base, q), ...monthRange(month) }
      : toParams(base, q)
    const wantDealerSeries = q.dealers.length > 0
    // Trend lines: with a merke filter /merker returns modell-level rows.
    // When no modell is picked we want per-MERKE lines, so drop the merke
    // filter from that one call and filter the rows client-side instead.
    const wantModellSeries = !wantDealerSeries && q.modell.length > 0
    const trendParams = wantModellSeries ? params : { ...params, merke: undefined, modell: undefined }
    setLoading(true)
    setError(null)
    // Dealer-comparison series come from a different endpoint (one row per
    // selected dealer, own monthly medians) — normalize into the same
    // StaatidMerkeRow shape MerkeScatter's trend lines already read, using
    // the dealer name as the series label (mirrors the merke/modell field).
    const merkerPromise: Promise<StaatidMerkerResponse> = wantDealerSeries
      ? fetchStaatidForhandlereTrend(params, q.dealers.map(d => d.orgNr)).then(r => ({
          total: r.data.total,
          drilldown: false,
          rows: r.data.rows.map(row => ({
            merke: row.dealer_org_navn ?? row.dealer_org_nr,
            modell: null,
            // The pair's own merke (#471) — only populated server-side once
            // merke[] is non-empty; unused outside groupBy 'dealer-merke'.
            pairMerke: row.merke,
            antall: row.antall,
            median_dager: row.median_dager,
            snitt_dager: row.snitt_dager,
            trend: row.trend,
          })),
        }))
      : fetchStaatidMerker(trendParams, [], 500, 0).then(r => r.data)
    Promise.all([
      merkerPromise,
      fetchStaatidOversikt(params),
      month
        ? fetchStaatidRoundtrips(params, 2000, 0).then(r => r.data)
        : loadScatterSample(params),
    ])
      .then(([m, o, r]) => {
        if (!alive()) return
        let rows = m.rows
        if (!wantDealerSeries) {
          if (wantModellSeries) rows = rows.filter(x => x.modell && q.modell.includes(x.modell))
          else if (q.merke.length) rows = rows.filter(x => x.merke && q.merke.includes(x.merke))
        }
        setMerker({ ...m, rows })
        setOversikt(o.data)
        setScatter(r)
      })
      .catch(e => { if (alive()) setError(e instanceof Error ? e.message : UNKNOWN_ERROR) })
      .finally(() => { if (alive()) setLoading(false) })
  }

  // Re-run the already-applied query when the shared period/postnummer
  // filter changes (top-level "Oppdater"). Without this, the scatter kept
  // showing results from the old period until the user noticed and clicked
  // "Vis resultater" again inside Spørring itself — every other Ståtid tab
  // refetches on period change, Spørring's draft/apply pattern just didn't.
  useEffect(() => {
    if (!applied) return
    setZoomMonth(null)
    runQuery(applied, null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseKey])

  function apply() {
    setApplied(draft)
    setZoomMonth(null)
    runQuery(draft, null)
  }

  function handleZoom(month: string | null) {
    if (!applied) return
    setZoomMonth(month)
    runQuery(applied, month)
  }

  // #471 dealer x merke pairs: capped at 3x3=9 lines, but ONLY once both
  // dimensions are already multi-select (2+) — a single merke with 8
  // dealers, or a single dealer with 8 merker, keep today's uncapped
  // behavior (MerkeScatter's own MAX_MERKE_SERIES governs those).
  const merkeCapReached = draft.merke.length >= 3 && draft.dealers.length >= 2
  const dealerCapReached = draft.dealers.length >= 3 && draft.merke.length >= 2

  return (
    <div className="space-y-4" data-testid="staatid-sporring">
      <div className="card p-4 space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <MultiSelectTypeahead
            label={t('sporring.merkeLabel')}
            placeholder={t('common.searchMerke')}
            options={merkeOptions}
            selected={draft.merke}
            onChange={merke => setDraft(d => ({ ...d, merke }))}
            disabled={merkeCapReached}
            disabledHint={t('sporring.capHint')}
          />
          <MultiSelectTypeahead
            label={t('sporring.modellLabel')}
            disabledHint={t('sporring.modellHint')}
            placeholder={t('sporring.searchModell')}
            options={modellOptions}
            selected={draft.modell}
            onChange={modell => setDraft(d => ({ ...d, modell }))}
            disabled={!draft.merke.length}
          />
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-3 items-end">
          {/* Forhandler typeahead (name or org.nr) — multi-select: each picked
              dealer becomes its own trend line for comparison (#274 follow-up). */}
          <div className="relative min-w-[260px]">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {t('sporring.dealerLabel')}
              {dealerCapReached ? ` — ${t('sporring.capHint')}` : ''}
            </p>
            {draft.dealers.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1">
                {draft.dealers.map(d => (
                  <span
                    key={d.orgNr}
                    className="inline-flex items-center gap-1 rounded-sm bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs px-2 py-1"
                  >
                    {d.navn ?? d.orgNr}
                    <button
                      type="button"
                      aria-label={t('common.remove', { name: d.navn ?? d.orgNr })}
                      onClick={() => setDraft(dr => ({ ...dr, dealers: dr.dealers.filter(x => x.orgNr !== d.orgNr) }))}
                      className="text-primary-500 hover:text-primary-700"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              type="text"
              data-testid="staatid-dealer-typeahead"
              disabled={dealerCapReached}
              value={dealerQuery}
              onFocus={() => setDealerOpen(true)}
              onChange={e => {
                setDealerQuery(e.target.value)
                setDealerOpen(true)
              }}
              onBlur={() => setTimeout(() => setDealerOpen(false), 150)}
              placeholder={t('sporring.searchDealer')}
              className="w-full text-sm rounded-sm border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {!dealerCapReached && dealerOpen && dealerOptions.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full max-h-48 overflow-auto rounded-sm border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-sm">
                {dealerOptions
                  .filter(o => !draft.dealers.some(d => d.orgNr === o.dealer_org_nr))
                  .map(o => (
                    <li key={o.dealer_org_nr}>
                      <button
                        type="button"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                          setDraft(d => ({
                            ...d,
                            dealers: [...d.dealers, { orgNr: o.dealer_org_nr, navn: o.dealer_org_navn }],
                          }))
                          setDealerQuery('')
                          setDealerOpen(false)
                        }}
                        className="block w-full text-left text-sm px-2 py-1 hover:bg-gray-50 dark:hover:bg-dark-border"
                      >
                        {o.dealer_org_navn ?? '—'}{' '}
                        <span className="font-mono text-xs text-gray-400">{o.dealer_org_nr}</span>
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          {/* Årsmodell range — type="text" + digit sanitization, not type="number":
              a controlled number input races the browser's own parsing (it
              returns "" for a still-mid-edit value), so keystrokes get
              dropped/reordered while typing (reported: "2010" -> "-2,5,10"). */}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('sporring.aarsmodell')}</p>
            <div className="flex items-center gap-1">
              <input
                type="text"
                inputMode="numeric"
                data-testid="staatid-aarsmodell-min"
                value={draft.aarsmodellMin ?? ''}
                onChange={e => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 4)
                  setDraft(d => ({ ...d, aarsmodellMin: digits ? Number(digits) : undefined }))
                }}
                placeholder={t('sporring.fra')}
                className="w-20 text-sm rounded-sm border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card px-2 py-1"
              />
              <span className="text-gray-400">–</span>
              <input
                type="text"
                inputMode="numeric"
                data-testid="staatid-aarsmodell-max"
                value={draft.aarsmodellMax ?? ''}
                onChange={e => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 4)
                  setDraft(d => ({ ...d, aarsmodellMax: digits ? Number(digits) : undefined }))
                }}
                placeholder={t('sporring.til')}
                className="w-20 text-sm rounded-sm border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card px-2 py-1"
              />
            </div>
          </div>

          {/* Antall ledd */}
          <ChipGroup
            label={t('sporring.antallLedd')}
            options={ANTALL_OPTIONS}
            selected={draft.antallTransfers}
            onChange={antallTransfers => setDraft(d => ({ ...d, antallTransfers }))}
          />

          {/* Drivlinje (#800) */}
          <ChipGroup
            label={t('filter.drivlinje')}
            options={DRIVLINJE_OPTIONS.map(o => ({ value: o.value, label: t(o.labelKey) }))}
            selected={draft.drivstoffGruppe}
            onChange={drivstoffGruppe => setDraft(d => ({ ...d, drivstoffGruppe }))}
          />

          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="staatid-sporring-apply"
              onClick={apply}
              className="text-sm rounded-sm bg-primary-600 text-white px-3 py-1 hover:bg-primary-700"
            >
              {t('sporring.apply')}
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(EMPTY_QUERY)
                setApplied(null)
                setMerker(null)
                setOversikt(null)
                setScatter(null)
                setZoomMonth(null)
              }}
              className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline"
            >
              {t('sporring.reset')}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="card p-4 text-red-600 dark:text-red-400 text-sm">
          {t('common.loadError', { error: error === UNKNOWN_ERROR ? t('common.unknownError') : error })}
        </div>
      )}

      {applied === null && !loading && (
        <div className="card p-6 text-sm text-gray-500 dark:text-gray-400 text-center">
          {t('sporring.empty')}
        </div>
      )}

      {(applied !== null || loading) && !error && (
        <>
          {oversikt && !loading && (
            <div className="flex flex-wrap gap-6 text-sm px-1" data-testid="staatid-sporring-stats">
              <div><span className="text-gray-500 dark:text-gray-400">{t('common.stat.sluttsalg')}</span> <strong>{oversikt.totalt_antall_salg.toLocaleString('nb-NO')}</strong></div>
              <div><span className="text-gray-500 dark:text-gray-400">{t('common.stat.dekningsgrad')}</span> <strong>{formatDekningsgrad(oversikt.dekningsgrad)}</strong></div>
              <div><span className="text-gray-500 dark:text-gray-400">{t('common.stat.median')}</span> <strong>{formatDager(oversikt.median_dager)} d</strong></div>
              <div><span className="text-gray-500 dark:text-gray-400">{t('common.stat.snitt')}</span> <strong>{formatDager(oversikt.snitt_dager)} d</strong></div>
            </div>
          )}
          <MerkeScatter
            rows={scatter?.rows ?? []}
            total={scatter?.total ?? 0}
            merker={merker?.rows ?? []}
            combinedTrend={oversikt?.trend ?? []}
            loading={loading}
            zoomMonth={zoomMonth}
            onZoomMonth={handleZoom}
            groupBy={groupBy}
            onPointClick={setTrailSluttSvvId}
            // Dealer comparison already scopes its data to a single active
            // merke filter (build_base_query applies merke[] to every
            // staatid endpoint) — this just makes that scoping visible in
            // the legend instead of a silent "BILIA" with no hint it's
            // Toyota-only. Multiple merker are combined into one number per
            // dealer server-side, so the suffix only applies when exactly
            // one is selected — labeling a blended line as a single merke
            // would be misleading.
            seriesLabelSuffix={groupBy === 'dealer' && applied?.merke.length === 1 ? applied.merke[0] : undefined}
          />
        </>
      )}

      <TrailPanel sluttSvvId={trailSluttSvvId} onClose={() => setTrailSluttSvvId(null)} />
    </div>
  )
}
